import { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash, ArrowUp, ArrowDown, FloppyDisk, UploadSimple, Palette, TextAa, Buildings, IdentificationCard } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";
import { useRole } from "@/hooks/useRole";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function BrandAssets() {
  const api = useApi();
  const { canManageContent } = useRole();
  const [brand, setBrand] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingBrand, setSavingBrand] = useState(false);
  const [collectionFilter, setCollectionFilter] = useState("all");
  const [uploadCollection, setUploadCollection] = useState("");
  const [uploadTags, setUploadTags] = useState("");
  const [uploading, setUploading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState("");
  const [companyInfoMeta, setCompanyInfoMeta] = useState(null);
  const [savingInfo, setSavingInfo] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    try {
      const [b, a, ci] = await Promise.all([api.get("/brand"), api.get("/brand/assets"), api.get("/company-profile")]);
      setBrand({ palette: b.data.palette || [], fonts: b.data.fonts || [], logo_text: b.data.logo_text || "", company_profile: b.data.company_profile || {} });
      setAssets(a.data);
      setCompanyInfo(ci.data.text || "");
      setCompanyInfoMeta({ updated_at: ci.data.updated_at, updated_by: ci.data.updated_by });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (!canManageContent) {
    return <div className="text-[#7A9090] p-8" data-testid="brand-no-access">You don't have access to the Brand Asset Library.</div>;
  }
  if (loading || !brand) return <div className="flex items-center justify-center h-96"><div className="text-[#3C4048] text-xl">Loading brand library…</div></div>;

  const setPalette = (p) => setBrand((b) => ({ ...b, palette: p }));
  const setFonts = (f) => setBrand((b) => ({ ...b, fonts: f }));

  const addColor = () => setPalette([...brand.palette, { name: "New colour", hex: "#5FA8B4" }]);
  const removeColor = (i) => setPalette(brand.palette.filter((_, idx) => idx !== i));
  const moveColor = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= brand.palette.length) return;
    const arr = [...brand.palette];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setPalette(arr);
  };
  const updateColor = (i, patch) => setPalette(brand.palette.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

  const setCP = (patch) => setBrand((b) => ({ ...b, company_profile: { ...(b.company_profile || {}), ...patch } }));

  const addFont = () => setFonts([...brand.fonts, { name: "Font", stack: "Font, sans-serif" }]);
  const removeFont = (i) => setFonts(brand.fonts.filter((_, idx) => idx !== i));
  const updateFont = (i, patch) => setFonts(brand.fonts.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));

  const saveBrand = async () => {
    setSavingBrand(true);
    try {
      const rawCp = brand.company_profile || {};
      const company_profile = { ...rawCp };
      ["core_values", "credentials", "services", "taglines", "never_use", "image_rules"].forEach((k) => {
        company_profile[k] = (rawCp[k] || []).map((s) => String(s).trim()).filter(Boolean);
      });
      await api.put("/brand", { palette: brand.palette, fonts: brand.fonts, logo_text: brand.logo_text, company_profile });
      toast.success("Brand saved — presets & AI tools updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingBrand(false); }
  };

  const saveCompanyInfo = async () => {
    setSavingInfo(true);
    try {
      const r = await api.put("/company-profile", { text: companyInfo });
      setCompanyInfoMeta({ updated_at: r.data.updated_at, updated_by: r.data.updated_by });
      toast.success("Company Profile saved — Shee will use this from now on");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingInfo(false); }
  };

  const doUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      const qs = new URLSearchParams();
      if (uploadCollection.trim()) qs.set("collection", uploadCollection.trim());
      if (uploadTags.trim()) qs.set("tags", uploadTags.trim());
      await api.post(`/brand/assets/upload${qs.toString() ? `?${qs}` : ""}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${files.length} asset(s)`);
      setUploadTags("");
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeAsset = async (id) => {
    try { await api.delete(`/brand/assets/${id}`); setAssets((a) => a.filter((x) => x.id !== id)); toast.success("Asset deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const collections = ["all", ...Array.from(new Set(assets.map((a) => a.collection).filter(Boolean)))];
  const shownAssets = collectionFilter === "all" ? assets : assets.filter((a) => a.collection === collectionFilter);

  return (
    <div className="space-y-8" data-testid="brand-assets-container">
      <div>
        <h1 className="text-4xl font-bold text-[#3C4048] mb-2 tracking-tight" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Brand Asset Library</h1>
        <p className="text-[#7A9090]">Your workspace's logos, product images, palette, fonts, brand voice and Company Profile — feeds the Design Studio presets and every AI tool, including Shee.</p>
      </div>

      <Tabs defaultValue="voice" className="w-full">
        <TabsList className="bg-[#f0f7f8] p-1 rounded-[12px]">
          <TabsTrigger value="voice" data-testid="tab-brand-voice" className="rounded-[10px] gap-1.5">
            <Buildings size={16} /> Brand Voice &amp; Guidelines
          </TabsTrigger>
          <TabsTrigger value="company" data-testid="tab-company-profile" className="rounded-[10px] gap-1.5">
            <IdentificationCard size={16} /> Company Profile
          </TabsTrigger>
        </TabsList>

        <TabsContent value="voice" className="space-y-8 mt-6">
      {/* Palette + Fonts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Palette size={20} className="text-[#3A7A8C]" weight="duotone" />
            <h2 className="text-lg font-bold text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>Brand Palette</h2>
          </div>
          <div className="space-y-2" data-testid="palette-editor">
            {brand.palette.map((c, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`palette-row-${i}`}>
                <input type="color" value={c.hex} onChange={(e) => updateColor(i, { hex: e.target.value })} className="w-9 h-9 rounded border border-[#e5e7eb] cursor-pointer" data-testid={`palette-color-${i}`} />
                <Input value={c.name} onChange={(e) => updateColor(i, { name: e.target.value })} className="flex-1 border-[#e5e7eb] text-[#3C4048] h-9" />
                <Input value={c.hex} onChange={(e) => updateColor(i, { hex: e.target.value })} className="w-24 border-[#e5e7eb] text-[#3C4048] h-9 font-mono text-xs" />
                <button onClick={() => moveColor(i, -1)} className="text-[#7A9090] hover:text-[#3C4048] p-2 -m-2" data-testid={`palette-move-up-${i}`}><ArrowUp size={15} /></button>
                <button onClick={() => moveColor(i, 1)} className="text-[#7A9090] hover:text-[#3C4048] p-2 -m-2" data-testid={`palette-move-down-${i}`}><ArrowDown size={15} /></button>
                <button onClick={() => removeColor(i)} className="text-[#ef4444] p-2 -m-2" data-testid={`palette-remove-${i}`}><Trash size={15} /></button>
              </div>
            ))}
          </div>
          <Button onClick={addColor} variant="outline" size="sm" className="mt-3 border-[#e5e7eb] text-[#3A7A8C] gap-1 rounded-[10px]" data-testid="add-color"><Plus size={14} /> Add colour</Button>
        </div>

        <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6">
          <div className="flex items-center gap-2 mb-4">
            <TextAa size={20} className="text-[#3A7A8C]" weight="duotone" />
            <h2 className="text-lg font-bold text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>Brand Fonts</h2>
          </div>
          <div className="space-y-2">
            {brand.fonts.map((f, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input value={f.name} onChange={(e) => updateFont(i, { name: e.target.value })} placeholder="Name" className="w-32 border-[#e5e7eb] text-[#3C4048] h-9" />
                <Input value={f.stack} onChange={(e) => updateFont(i, { stack: e.target.value })} placeholder="CSS stack" className="flex-1 border-[#e5e7eb] text-[#3C4048] h-9 text-xs" />
                <button onClick={() => removeFont(i)} className="text-[#ef4444] p-2 -m-2" data-testid={`font-remove-${i}`}><Trash size={15} /></button>
              </div>
            ))}
          </div>
          <Button onClick={addFont} variant="outline" size="sm" className="mt-3 border-[#e5e7eb] text-[#3A7A8C] gap-1 rounded-[10px]" data-testid="add-font"><Plus size={14} /> Add font</Button>
          <div className="mt-4">
            <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Logo wordmark text</Label>
            <Input value={brand.logo_text} onChange={(e) => setBrand((b) => ({ ...b, logo_text: e.target.value }))} className="border-[#e5e7eb] text-[#3C4048]" data-testid="logo-text" />
          </div>
        </div>
      </div>

      {/* Brand Voice & Guidelines */}
      <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6" data-testid="brand-voice-editor">
        <div className="flex items-center gap-2 mb-1">
          <Buildings size={20} className="text-[#3A7A8C]" weight="duotone" />
          <h2 className="text-lg font-bold text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>Brand Voice &amp; Guidelines</h2>
        </div>
        <p className="text-[#7A9090] text-sm mb-5">Your brand's voice, values and standards. Shee and every AI tool use this to keep captions, ads and images on-brand for this workspace. Looking for contact info, hours, team or booking links instead? That lives on the <strong>Company Profile</strong> tab.</p>
        <div className="space-y-5">
          <div>
            <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Brand statement</Label>
            <Input value={(brand.company_profile || {}).brand_statement || ""} onChange={(e) => setCP({ brand_statement: e.target.value })} className="border-[#e5e7eb] text-[#3C4048]" placeholder="Where Clinical Expertise Meets Lasting Transformation." data-testid="cp-brand-statement" />
          </div>
          <div>
            <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Positioning</Label>
            <textarea value={(brand.company_profile || {}).positioning || ""} onChange={(e) => setCP({ positioning: e.target.value })} rows={3}
              className="w-full border border-[#e5e7eb] rounded-md text-[#3C4048] text-sm p-2.5 focus:outline-none focus:ring-2 focus:ring-[#5FA8B4]/40"
              placeholder="How the brand should be positioned (e.g. a clinical practice — not a spa)." data-testid="cp-positioning" />
          </div>
          <div>
            <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Voice &amp; tone</Label>
            <textarea value={(brand.company_profile || {}).voice_tone || ""} onChange={(e) => setCP({ voice_tone: e.target.value })} rows={3}
              className="w-full border border-[#e5e7eb] rounded-md text-[#3C4048] text-sm p-2.5 focus:outline-none focus:ring-2 focus:ring-[#5FA8B4]/40"
              placeholder="Personality and tone of voice." data-testid="cp-voice-tone" />
          </div>
          {[
            { key: "core_values", label: "Core values", hint: "One per line" },
            { key: "credentials", label: "Credentials & proof points", hint: "One per line — used as the credibility beat in captions & ads" },
            { key: "services", label: "Services", hint: "One per line" },
            { key: "taglines", label: "Taglines", hint: "One per line" },
            { key: "never_use", label: "Never use — banned language & claims", hint: "One per line" },
            { key: "image_rules", label: "Image rules — visual do's & don'ts", hint: "One per line — steers AI image generation" },
          ].map((f) => (
            <div key={f.key}>
              <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">{f.label}</Label>
              <textarea
                value={((brand.company_profile || {})[f.key] || []).join("\n")}
                onChange={(e) => setCP({ [f.key]: e.target.value.split("\n") })}
                rows={4}
                className="w-full border border-[#e5e7eb] rounded-md text-[#3C4048] text-sm p-2.5 focus:outline-none focus:ring-2 focus:ring-[#5FA8B4]/40"
                data-testid={`cp-${f.key.replace(/_/g, "-")}`}
              />
              <p className="text-[#a0adad] text-[11px] mt-1">{f.hint}</p>
            </div>
          ))}
        </div>
      </div>

      <Button onClick={saveBrand} disabled={savingBrand} className="bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white gap-2 rounded-[12px]" data-testid="save-brand">
        <FloppyDisk size={16} /> {savingBrand ? "Saving…" : "Save brand"}
      </Button>

      {/* Upload */}
      <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6">
        <h2 className="text-lg font-bold text-[#3C4048] mb-4" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>Upload assets</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <Input value={uploadCollection} onChange={(e) => setUploadCollection(e.target.value)} placeholder="Collection (e.g. Equipment, Clinic interiors)" className="border-[#e5e7eb] text-[#3C4048]" data-testid="upload-collection" />
          <Input value={uploadTags} onChange={(e) => setUploadTags(e.target.value)} placeholder="Extra tags (comma-separated)" className="border-[#e5e7eb] text-[#3C4048]" data-testid="upload-asset-tags" />
        </div>
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); doUpload(e.dataTransfer.files); }}
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-[#d5dbdc] rounded-[12px] p-8 text-center cursor-pointer hover:border-[#5FA8B4] hover:bg-[#f0f7f8] transition-colors"
          data-testid="asset-dropzone"
        >
          <UploadSimple size={26} className="text-[#3A7A8C] mx-auto mb-2" />
          <p className="text-[#7A9090] text-sm">{uploading ? "Uploading…" : "Drag & drop images here, or click to browse"}</p>
          <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={(e) => doUpload(e.target.files)} data-testid="asset-file-input" />
        </div>
      </div>

      {/* Assets */}
      <div>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {collections.map((c) => (
            <button key={c} onClick={() => setCollectionFilter(c)} data-testid={`collection-${c}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${collectionFilter === c ? "bg-[#5FA8B4] text-white border-[#5FA8B4]" : "bg-white text-[#3C4048] border-[#e5e7eb] hover:border-[#5FA8B4]"}`}>
              {c === "all" ? "All assets" : c}
            </button>
          ))}
        </div>
        {shownAssets.length === 0 ? (
          <div className="rounded-[12px] border border-dashed border-[#d5dbdc] bg-[#fafafa] p-10 text-center"><p className="text-[#7A9090]">No assets yet — upload logos and product images above.</p></div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {shownAssets.map((a) => (
              <div key={a.id} data-testid={`asset-${a.id}`} className="bg-white border border-[#e5e7eb] rounded-[12px] overflow-hidden group">
                <div className="aspect-square bg-[#f5f5f5] overflow-hidden flex items-center justify-center">
                  <img src={a.file_url} alt={a.filename} className="w-full h-full object-contain" />
                </div>
                <div className="p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-[#3A7A8C] bg-[#f0f7f8] px-2 py-0.5 rounded">{a.kind}</span>
                    <button onClick={() => removeAsset(a.id)} className="text-[#ef4444] opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-asset-${a.id}`}><Trash size={14} /></button>
                  </div>
                  <p className="text-[#3C4048] text-xs font-medium truncate mt-2">{a.filename}</p>
                  {a.collection && <p className="text-[#7A9090] text-[11px] mt-0.5">{a.collection}</p>}
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {a.tags.slice(0, 4).map((t) => <span key={t} className="text-[10px] text-[#7A9090] bg-[#eef1f2] px-1.5 py-0.5 rounded">{t}</span>)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
        </TabsContent>

        <TabsContent value="company" className="mt-6">
          <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6" data-testid="company-profile-editor">
            <div className="flex items-center gap-2 mb-1">
              <IdentificationCard size={20} className="text-[#3A7A8C]" weight="duotone" />
              <h2 className="text-lg font-bold text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>Company Profile</h2>
            </div>
            <p className="text-[#7A9090] text-sm mb-1">
              The full real-world reference document for this business — contact info, address, hours, team &amp;
              booking links, services, credentials, social platform rules, content footers. Shee reads this at the
              start of every session and treats it as the authoritative source for any company fact — she'll never
              invent a phone number, hours or a booking link that isn't in here.
            </p>
            {companyInfoMeta?.updated_at && (
              <p className="text-[#a0adad] text-xs mb-4">Last updated {new Date(companyInfoMeta.updated_at).toLocaleString()}</p>
            )}
            <textarea
              value={companyInfo}
              onChange={(e) => setCompanyInfo(e.target.value)}
              rows={28}
              placeholder="Paste or write your full Company Profile document here — contact info, hours, team, services, credentials, social rules, footers, anything Shee should always know about this business."
              className="w-full border border-[#e5e7eb] rounded-md text-[#3C4048] text-sm p-3 font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#5FA8B4]/40"
              data-testid="company-profile-textarea"
            />
            <Button onClick={saveCompanyInfo} disabled={savingInfo} className="mt-4 bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white gap-2 rounded-[12px]" data-testid="save-company-profile">
              <FloppyDisk size={16} /> {savingInfo ? "Saving…" : "Save Company Profile"}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
